import { ListContentNode } from '@helo/cms'

const hasLazyItemsGetter = (itemList: ListContentNode) => {
  const itemsDescriptor = Object.getOwnPropertyDescriptor(itemList, 'items')
  return itemsDescriptor?.get != null
}

const getRawListDataCount = (itemList: ListContentNode) =>
  Array.isArray(itemList.data) ? itemList.data.length : 0

const resolveListNodeItemData = (
  itemList: ListContentNode | null | undefined,
): unknown[] | null | undefined => {
  if (itemList === undefined) return undefined
  if (itemList === null) return null

  if (itemList.data === undefined || itemList.data === null) {
    if (hasLazyItemsGetter(itemList)) {
      return undefined
    }

    if (itemList.items !== undefined && itemList.items !== null) {
      return itemList.items
    }

    return undefined
  }

  const resolvedItems = itemList.items
  const rawCount = getRawListDataCount(itemList)

  if (Array.isArray(resolvedItems) && resolvedItems.length > 0) {
    if (rawCount === 0 || resolvedItems.length >= rawCount) {
      return resolvedItems
    }
  }

  if (Array.isArray(itemList.data) && itemList.data.length > 0) {
    return itemList.data
  }

  if (resolvedItems !== undefined) {
    return resolvedItems
  }

  return itemList.data
}

const isHydratedItemsBehindData = (itemList: ListContentNode) => {
  if (itemList.data === undefined || itemList.data === null) {
    return false
  }

  const rawCount = getRawListDataCount(itemList)
  if (rawCount === 0) {
    return false
  }

  const resolvedItems = itemList.items
  return !Array.isArray(resolvedItems) || resolvedItems.length < rawCount
}

const isEmptyListData = (itemList: ListContentNode) =>
  Array.isArray(itemList.data) && itemList.data.length === 0

const isResolvedEmptyList = (itemList: ListContentNode) =>
  isEmptyListData(itemList) &&
  Array.isArray(itemList.items) &&
  itemList.items.length === 0

export const ListContentNodeParts = {
  getRawListDataCount: (
    itemList: ListContentNode | unknown[] | null | undefined,
  ) => {
    if (Array.isArray(itemList)) return itemList.length
    if (!itemList || typeof itemList !== 'object') return 0
    return getRawListDataCount(itemList as ListContentNode)
  },
  resolveListItemData: (
    itemList: ListContentNode | unknown[] | null | undefined,
  ): unknown[] | null | undefined => {
    if (Array.isArray(itemList)) return itemList
    return resolveListNodeItemData(itemList)
  },
  getExpectedItemCount: (
    itemList: ListContentNode | unknown[] | null | undefined,
    fallback = 3,
  ) => {
    if (Array.isArray(itemList)) return itemList.length
    if (itemList === undefined || itemList === null) return fallback

    const rawCount = getRawListDataCount(itemList as ListContentNode)
    return rawCount > 0 ? rawCount : fallback
  },
  getPendingSkeletonCount: (
    itemList: ListContentNode | unknown[] | null | undefined,
    isLoading?: boolean,
    fallback = 3,
  ) => {
    if (ListContentNodeParts.isListPending(itemList, isLoading)) {
      const expectedCount = ListContentNodeParts.getExpectedItemCount(
        itemList,
        fallback,
      )
      const hydratedItems =
        ListContentNodeParts.resolveListItemData(itemList) ?? []
      const hydratedCount = Array.isArray(hydratedItems)
        ? hydratedItems.length
        : 0

      return Math.max(0, expectedCount - hydratedCount)
    }

    return 0
  },
  getSkeletonRenderCount: (
    itemList: ListContentNode | unknown[] | null | undefined,
    options?: {
      isLoading?: boolean
      fallback?: number
      disableLoadingSkeletons?: boolean
    },
  ) => {
    const { isLoading, fallback = 3, disableLoadingSkeletons } = options ?? {}

    if (disableLoadingSkeletons) return 0

    if (ListContentNodeParts.isConfirmedEmpty(itemList, isLoading)) {
      return 0
    }

    if (!ListContentNodeParts.isListPending(itemList, isLoading)) {
      return 0
    }

    const hydratedItems = ListContentNodeParts.resolveListItemData(itemList)
    const hydratedCount = Array.isArray(hydratedItems)
      ? hydratedItems.length
      : 0
    const pendingCount = ListContentNodeParts.getPendingSkeletonCount(
      itemList,
      isLoading,
      fallback,
    )

    if (pendingCount > 0) return pendingCount
    if (hydratedCount > 0) return 0
    return fallback
  },
  isListPending: (
    itemList: ListContentNode | unknown[] | null | undefined,
    isLoading?: boolean,
  ) => {
    if (isLoading) return true
    if (itemList === undefined) return false
    if (itemList === null) return false
    if (Array.isArray(itemList)) return false

    const listNode = itemList as ListContentNode

    if (listNode.data === undefined || listNode.data === null) {
      if (hasLazyItemsGetter(listNode)) return true
      return listNode.items === undefined || listNode.items === null
    }

    if (isEmptyListData(listNode)) {
      if (hasLazyItemsGetter(listNode)) {
        return !Array.isArray(listNode.items)
      }
      if (listNode.items === undefined || listNode.items === null) {
        return true
      }
      return false
    }

    if (!isLoading) {
      const displayItems = resolveListNodeItemData(listNode)
      const rawCount = getRawListDataCount(listNode)

      if (
        rawCount > 0 &&
        Array.isArray(displayItems) &&
        displayItems.length >= rawCount
      ) {
        return false
      }
    }

    if (isHydratedItemsBehindData(listNode)) {
      return true
    }

    return false
  },
  isConfirmedEmpty: (
    itemList: ListContentNode | unknown[] | null | undefined,
    isLoading?: boolean,
  ) => {
    if (isLoading) return false
    if (itemList === undefined || itemList === null) return false
    if (Array.isArray(itemList)) return itemList.length === 0

    const listNode = itemList as ListContentNode

    if (ListContentNodeParts.isListPending(listNode, false)) {
      return false
    }

    if (isResolvedEmptyList(listNode)) {
      return true
    }

    const items = ListContentNodeParts.resolveListItemData(itemList)
    return items !== undefined && items !== null && items.length === 0
  },
}
